import { Fragment } from 'react';
import {
  Grid,
  Box,
  Typography,
  Table,
  TableBody,
  TableRow,
  TableCell,
  RadioGroup,
  Radio,
  useTheme,
} from '@mui/material';
import SectionTitle from '../../components/SectionTitle';
import { IFormValues, IFormWatch } from 'routes/Main/type';
import useTableForm from '../../hooks/useTableForm';
import MuiRadioGroupSub from './MuiRadioGroupSub';

interface Props extends IFormValues, IFormWatch {
  disabled?: boolean;
}

const BDIContents = (props: Props) => {
  const { palette } = useTheme();

  const { radioGroup, sumValues } = useTableForm(props);
  const { disabled, watch, setValue, getValues } = props;

  const titles = [
    '1',
    '2',
    '3',
    '4',
    '5',
    '6',
    '7',
    '8',
    '9',
    '10',
    '11',
    '12',
    '13',
    '14',
    '15',
    '16',
    '17',
    '18',
    '19',
    '20',
    '21',
  ];

  const answers = [
    [
      'I do not feel sad.',
      'I feel sad.',
      'I am sad all the time and I can’t snap out of it.',
      'I am so sad and unhappy that I can’t stand it.',
    ],
    [
      'I am not particularly discouraged about the future.',
      'I feel discouraged about the future.',
      'I feel I have nothing to look forward to.',
      'I feel the future is hopeless and that things cannot improve.',
    ],
    [
      'I do not feel like a failure.',
      'I feel I have failed more than the average person.',
      'As I look back on my life, all I can see is a lot of failures.',
      'I feel I am a complete failure as a person.',
    ],
    [
      'I get as much satisfaction out of things as I used to.',
      'I don’t enjoy things the way I used to.',
      'I don’t get real satisfaction out of anything anymore.',
      'I am dissatisfied or bored with everything.',
    ],
    [
      'I don’t feel particularly guilty.',
      'I feel guilty a good part of the time.',
      'I feel quite guilty most of the time.',
      'I feel guilty all of the time.',
    ],
    [
      'I don’t feel I am being punished.',
      'I feel I may be punished.',
      'I expect to be punished.',
      'I feel I am being punished.',
    ],
    [
      'I don’t feel disappointed in myself.',
      'I am disappointed in myself.',
      'I am disgusted with myself.',
      'I hate myself.',
    ],
    [
      'I don’t feel I am any worse than anybody else.',
      'I am critical of myself for my weaknesses or mistakes.',
      'I blame myself all the time for my faults.',
      'I blame myself for everything bad that happens.',
    ],
    [
      'I don’t have any thoughts of killing myself.',
      'I have thoughts of killing myself, but I would not carry them out.',
      'I would like to kill myself.',
      'I would kill myself if I had the chance.',
    ],
    [
      'I don’t cry any more than usual.',
      'I cry more now than I used to.',
      'I cry all the time now.',
      'I used to be able to cry, but now I can’t cry even though I want to.',
    ],
    [
      'I am no more irritated by things that I ever was.',
      'I am slightly more irritated now than usual.',
      'I am quite annoyed or irritated a good deal of the time.',
      'I feel irritated all the time.',
    ],
    [
      'I have not lost interest in other people.',
      'I am less interested in other people than I used to be.',
      'I have lost most of my interest in other people.',
      'I have lost all of my interest in other people.',
    ],
    [
      'I make decisions about as well as I ever could.',
      'I put off making decisions more than I used to.',
      'I have greater difficulty in making decisions more than I used to.',
      'I can’t make decisions at all anymore.',
    ],
    [
      'I don’t feel that I look any worse than I used to.',
      'I am worried that I am looking old or unattractive.',
      'I feel that there are permanent changes in my appearance that make me look unattractive.',
      'I believe that I look ugly.',
    ],
    [
      'I can work about as well as before.',
      'It takes an extra effort to get started at doing something.',
      'I have to push myself very hard to do anything.',
      'I can’t do any work at all.',
    ],
    [
      'I can sleep as well as usual.',
      'I don’t sleep as well as I used to.',
      'I wake up 1-2 hours earlier than usual and find it hard to get back to sleep.',
      'I wake up several hours earlier that I used to and cannot get back to sleep.',
    ],
    [
      'I don’t get more tired than usual.',
      'I get tired more easily than I used to.',
      'I get tired from doing almost anything.',
      'I am too tired to do anything.',
    ],
    [
      'My appetite is no worse than usual.',
      'My appetite is not as good as it used to be.',
      'My appetite is much worse now.',
      'I have no appetite at all anymore.',
    ],
    [
      'I haven’t lost much weight, if any, lately.',
      'I have lost more than five pounds.',
      'I have lost more than ten pounds.',
      'I have lost more than fifteen pounds.',
    ],
    [
      'I am no more worried about my health than usual.',
      'I am worried about my physical problems like aches, pains, upset stomach, or constipation.',
      'I am very worried about physical problems and it’s hard to think of much else.',
      'I am so worried about my physical problems that I cannot think of anything else.',
    ],
    [
      'I have not noticed any recent change in my interest in sex.',
      'I am less interested in sex than I used to be.',
      'I have almost no interest in sex.',
      'I have lost interest in sex completely.',
    ],
  ];

  const watchSumValues = () => {
    const values = titles.map((_, i) =>
      watch(`content[${i}]`) ? Number(watch(`content[${i}]`)) : 0
    );
    let sum = sumValues(values);

    setValue('sum', sum);
    return sum;
  };

  return (
    <Fragment>
      {titles.map((title, idx) => (
        <>
          <SectionTitle title={title} />
          <Grid item xs={12}>
            <RadioGroup
              name={title}
              defaultValue={Number(getValues(`content[${idx}]`))}
            >
              <Table aria-label="simple table">
                <TableBody>
                  {answers[idx].map((answer, i) => {
                    return (
                      <TableRow key={answer}>
                        <TableCell sx={{ width: '80%' }}>{answer}</TableCell>
                        <TableCell sx={{ width: '10%' }}>{`${i}`}</TableCell>
                        <TableCell>
                          <Radio
                            disabled={disabled}
                            name={title}
                            value={i}
                            sx={{ height: '44px' }}
                            onChange={() => setValue(`content[${idx}]`, i)}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </RadioGroup>
          </Grid>
        </>
      ))}

      <Grid item xs={12}>
        <Box display={'flex'} flexDirection={'column'} alignItems={'flex-end'}>
          <Typography
            gutterBottom
            minWidth={115}
            fontWeight={700}
            variant="subtitle1"
          >
            Total : {watchSumValues()} point
          </Typography>
          <Typography
            minWidth={115}
            variant="caption"
            sx={{ color: `${palette.primary.main}` }}
          >
            <Typography variant="inherit">
              <Box component={'strong'} mr={0.5}>
                0-10
              </Box>
              : These ups and downs are considered normal
            </Typography>
            <Typography variant="inherit">
              <Box component={'strong'} mr={0.5}>
                11-16
              </Box>
              : Mild mood disturbance
            </Typography>
            <Typography variant="inherit">
              <Box component={'strong'} mr={0.5}>
                17-20
              </Box>
              : Borderline clinical depression
            </Typography>
            <Typography variant="inherit">
              <Box component={'strong'} mr={0.5}>
                21-30
              </Box>
              : Moderate depression
            </Typography>
            <Typography variant="inherit">
              <Box component={'strong'} mr={0.5}>
                31-40
              </Box>
              : Severe depression
            </Typography>
            <Typography variant="inherit">
              <Box component={'strong'} mr={0.5}>
                over 40
              </Box>
              : Extreme depression
            </Typography>
          </Typography>
        </Box>
      </Grid>
    </Fragment>
  );
};

export default BDIContents;
